import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { GROQButton } from '@/sanity/lib/definitions';
import { stegaClean } from '@sanity/client/stega';
import { Slot } from '@radix-ui/react-slot';
import { CTAWrapper } from '@/components/global/utils/CTAWrapper';
import { iconMap } from '@/lib/iconMap';

const buttonVariants = cva('button cta', {
  variants: {
    variant: {
      default: 'button-default',
      'button-green': 'button-green',
      'button-green-shape': 'button-green-shape',
      'black-outline': 'black-outline',
      'white-outline': 'white-outline',
    },
    action: {
      link: '',
      popup: '',
      anchor: '',
      none: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    action: 'none',
  },
});

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, action, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const children = props.children;
    
    return (
      <Comp
        className={cn('group', buttonVariants({ variant, className }))}
        data-type={variant}
        data-action={action}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export interface CTAButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  data: GROQButton;
  date?: boolean;
  lang?: 'en' | 'de';
}

const CTAButton = React.forwardRef<HTMLButtonElement, CTAButtonProps>(
  (
    {
      className,
      data,
      date = false,
      lang = 'de',
      ...props
    },
    ref,
  ) => {
    const cleanedData = stegaClean(data ?? {}) ?? null;

    const type = (cleanedData?.type ?? 'default') as
      | 'default'
      | 'button-green'
      | 'black-outline'
      | 'white-outline';

    const hasIcon = cleanedData?.cta?.icon ?? 'none';
    const icon = iconMap[hasIcon as keyof typeof iconMap];
    const cta = data?.cta;
    const enableMultiLang = cta?.enableMultiLang ?? false;

    const english = enableMultiLang ? cta?.englishTitle : cta?.title;

    const title = lang === 'de' ? cta?.title : english;

    const action = cleanedData?.cta?.action ?? null;

    const currentYear = new Date().getFullYear().toString();
    const firstDigits = currentYear.slice(0, 2);
    const lastDigits = currentYear.slice(2, 4);

    const buttonElement = (
      <Button
        data-has-icon={hasIcon !== 'none' ? 'true' : 'false'}
        className="relative w-full group flex items-center justify-center overflow-clip"
        variant={type as 'default' | 'button-green' | 'black-outline' | 'white-outline'}
        action={action ?? 'none'}
        ref={ref}
        {...props}
      >
        <div className="button-bg absolute inset-0 duration-500 transition-all" />

        <div className="relative leading-none">
          <div className="button-text">
            {title}
          </div>
        </div>

        {icon && (
          <div
            aria-hidden="true"
            className="button-icon !w-[3rem] !h-[3rem]"
          >
            {icon}
          </div>
        )}
      </Button>
    );

    // Blur and ghost buttons should be full-width on mobile
    const wrapperClassName = cn(
      'group inline-block relative z-10',
      type !== 'default' && 'w-full md:w-auto',
      className,
    );

    return (
      <CTAWrapper cta={cleanedData?.cta} className={wrapperClassName}>
        {date ? (
          <div className="flex items-center gap-[1rem]">
            <span className="w-[2rem] flex-shrink-0 text-[#B78768] font-instrument-serif text-[2.25rem] flex items-center justify-center">
              {firstDigits}
            </span>
            {buttonElement}
            <span className="w-[2rem] flex-shrink-0 text-[#B78768] font-instrument-serif text-[2.25rem] flex items-center justify-center">
              {lastDigits}
            </span>
          </div>
        ) : (
          buttonElement
        )}
      </CTAWrapper>
    );
  },
);
CTAButton.displayName = 'CTAButton';

export { Button, buttonVariants, CTAButton };
